import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HardHat, LayoutDashboard, FolderKanban, LogOut, Search, Ruler, Sun, Moon, FileStack, Ticket, AlertTriangle, Image as ImageIcon, FileText, FolderArchive, Settings, Users, Building2, Compass, ClipboardCheck, Info, CheckCircle2, CloudDownload, ListChecks, Trash2, LifeBuoy, RefreshCw, LayoutGrid, ChevronDown, SlidersHorizontal, UserCheck, Bug, CreditCard, BadgeEuro, Scale, ShieldCheck } from "lucide-react";
import { LegalFooter } from "@/components/LegalFooter";
import { can } from "@/lib/perms";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTour } from "@/context/TourContext";
import NotificationBell from "@/components/NotificationBell";
import OfflineBanner from "@/components/OfflineBanner";
import OfflinePrepProgress from "@/components/OfflinePrepProgress";
import VersionBanner from "@/components/VersionBanner";
import { refreshApp } from "@/components/RefreshAppButton";
import { usePendingPhotoTotal } from "@/components/PendingPhotoBadge";
import NavSettingsDialog, { useNavPrefs, sortNav } from "@/components/NavSettingsDialog";
import AccountsDialog, { useMyAccounts } from "@/components/AccountsDialog";
import { ACCESS_CHANGED } from "@/pages/AccessRequests";
import InstallPrompt from "@/components/InstallPrompt";
import ProblemReportDialog from "@/components/ProblemReportDialog";
import BillingBanner from "@/components/BillingBanner";
import { prefetchAll } from "@/lib/offlinePrefetch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { APP_VERSION, BUILD_DATE, CHANGELOG, CHANGELOG_MAX } from "@/lib/version";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/aufgaben", label: "Meine Aufgaben", short: "Aufgaben", id: "aufgaben", icon: ListChecks },
  { to: "/pruefungen", label: "Prüfungen", icon: ClipboardCheck },
  { to: "/projects", label: "Projekte", icon: FolderKanban },
  { to: "/overview/plans", label: "Baupläne", icon: FileStack, more: true },
  { to: "/overview/tickets", label: "Tickets", icon: Ticket },
  { to: "/overview/defects", label: "Mängel", icon: AlertTriangle },
  { to: "/quantities", label: "Mengen", icon: Ruler, more: true },
  { to: "/overview/photos", label: "Fotos", icon: ImageIcon },
  { to: "/overview/reports", label: "Berichte", icon: FileText, more: true },
  { to: "/overview/documents", label: "Dokumente", icon: FolderArchive, more: true },
  { to: "/loeschanfragen", label: "Löschanfragen", short: "Anfragen", id: "loeschanfragen", icon: Trash2, perm: "defect:approve" },
];

export const DR_CHANGED = "pd-deletion-requests-changed";

function useAccessRequestCount(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) { setCount(0); return undefined; }
    const load = () => { if (navigator.onLine) api.get("/access-requests/count").then((r) => setCount(r.data.count)).catch(() => {}); };
    load();
    const t = setInterval(load, 120000);
    window.addEventListener(ACCESS_CHANGED, load);
    return () => { clearInterval(t); window.removeEventListener(ACCESS_CHANGED, load); };
  }, [enabled]);
  return count;
}

function useDeletionRequestCount(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) { setCount(0); return undefined; }
    const load = () => { if (navigator.onLine) api.get("/defects/deletion-requests/count").then((r) => setCount(r.data.count)).catch(() => {}); };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener(DR_CHANGED, load);
    window.addEventListener("focus", load);
    return () => { clearInterval(t); window.removeEventListener(DR_CHANGED, load); window.removeEventListener("focus", load); };
  }, [enabled]);
  return count;
}

const NavBadge = ({ n, testid, mobile, title, color = "bg-red-600" }) => (
  <span data-testid={testid} title={title || `${n} offene Löschanfrage${n === 1 ? "" : "n"}`}
    className={`absolute ${mobile ? "top-1 right-4" : "top-0 right-0"} min-w-[18px] h-[18px] px-1 rounded-full ${color} text-white text-[10px] font-bold leading-[18px] text-center`}>
    {n > 99 ? "99+" : n}
  </span>
);

// Collapsible "Mehr" menu for the less-used sections (Baupläne, Mengen, Berichte, Dokumente).
function MoreMenu({ items, mobile, pathname }) {
  const active = items.some((n) => pathname.startsWith(n.to));
  const cls = mobile
    ? `relative shrink-0 w-[76px] min-h-[58px] flex flex-col items-center justify-center gap-0.5 py-2 transition-colors ${active ? "text-primary" : "text-slate-500"}`
    : `relative flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${active ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cls} data-testid={mobile ? "mobilenav-mehr" : "nav-mehr"}>
          <LayoutGrid className={mobile ? "h-5 w-5" : "h-4 w-4 shrink-0"} />
          {mobile ? <span className="text-[10px] font-semibold">Mehr</span> : <span className="hidden xl:inline">Mehr</span>}
          {!mobile && <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={mobile ? "end" : "start"} side={mobile ? "top" : "bottom"} className="w-48">
        {items.map((n) => (
          <DropdownMenuItem key={n.to} asChild>
            <Link to={n.to} data-testid={`${mobile ? "mobilenav" : "nav"}-more-${n.label.toLowerCase()}`}
              className={pathname.startsWith(n.to) ? "text-primary font-semibold" : ""}>
              <n.icon className="h-4 w-4 mr-2" />{n.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Main items with the "Mehr" menu inserted right before Löschanfragen (or at the end).
function withMore(items, render, renderMore) {
  const main = items.filter((n) => !n.more);
  const more = items.filter((n) => n.more);
  const idx = main.findIndex((n) => n.id === "loeschanfragen");
  const at = idx === -1 ? main.length : idx;
  const out = main.map(render);
  if (more.length) out.splice(at, 0, <React.Fragment key="__more">{renderMore(more)}</React.Fragment>);
  return out;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navPrefs = useNavPrefs();
  const navMore = navPrefs?.more || null;
  const isMore = (n) => (n.to !== "/dashboard" && (navMore ? navMore.includes(n.to) : !!n.more));
  const allowedNav = NAV.filter((n) => !n.perm || can(user, n.perm));
  const orderedNav = [allowedNav[0], ...sortNav(allowedNav.slice(1), navPrefs?.order)];
  const navItems = orderedNav.map((n) => ({ ...n, more: isMore(n) }));
  const [showNavSettings, setShowNavSettings] = React.useState(false);
  const [accounts, reloadAccounts] = useMyAccounts(user);
  const [accountsOpen, setAccountsOpen] = React.useState(null); // null | "menu" | "chooser"
  const [showProblem, setShowProblem] = React.useState(false);
  React.useEffect(() => {
    // After sign-in with several accounts on the same e-mail: ask once per browser session.
    if (accounts.length > 1 && !sessionStorage.getItem("accountsChosen")) {
      sessionStorage.setItem("accountsChosen", "1");
      setAccountsOpen("chooser");
    }
  }, [accounts.length]);
  const drCount = useDeletionRequestCount(can(user, "defect:approve"));
  const accessCount = useAccessRequestCount(!!user?.is_platform_admin);
  const badgeFor = (n) => (n.id === "loeschanfragen" && drCount > 0 ? drCount : 0);
  const pendingPhotos = usePendingPhotoTotal();
  const photoBadge = (n, mobile) => (n.to === "/overview/defects" && pendingPhotos > 0
    ? <NavBadge n={pendingPhotos} mobile={mobile} color="bg-amber-500" testid={`${mobile ? "mobilenav" : "nav"}-pending-photos-count`} title={`${pendingPhotos} Foto(s) noch nicht übertragen`} />
    : null);
  const { startTour } = useTour();
  const location = useLocation();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();
  const isAdmin = user?.role === "admin";
  const [dark, setDark] = React.useState(() => document.documentElement.classList.contains("dark"));
  const [showVersion, setShowVersion] = React.useState(false);
  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Once authenticated + online, pull the whole workspace (data + photos) into the
  // offline caches so the app is fully usable without a connection.
  React.useEffect(() => {
    if (navigator.onLine) prefetchAll();
    const onOnline = () => prefetchAll();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <OfflineBanner />
      <OfflinePrepProgress />
      <VersionBanner onDetails={() => setShowVersion(true)} />
      <InstallPrompt />
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-logo">
              <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center shadow">
                <HardHat className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-head font-extrabold tracking-tight hidden sm:block whitespace-nowrap">PrüfDoc Pro</span>
            </Link>
            <nav className="hidden md:flex items-center gap-0.5 overflow-x-auto max-w-full">
              {withMore(navItems, (n) => {
                const active = location.pathname.startsWith(n.to);
                return (
                  <Link key={n.to} to={n.to} data-testid={`nav-${n.id || n.label.toLowerCase()}`}
                    className={`relative flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${active ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-100"}`}>
                    <n.icon className="h-4 w-4 shrink-0" />
                    <span className="hidden xl:inline">{n.short || n.label}</span>
                    {badgeFor(n) > 0 && <NavBadge n={badgeFor(n)} testid="nav-loeschanfragen-count" />}
                    {photoBadge(n, false)}
                  </Link>
                );
              }, (more) => <MoreMenu items={more} pathname={location.pathname} />)}
            </nav>
          </div>
          <div className="flex items-center gap-1">
            <Link to="/search" data-testid="nav-suche" title="Suche" className="p-2 rounded-lg hover:bg-slate-100 text-slate-600">
              <Search className="h-5 w-5" />
            </Link>
            <button onClick={toggleTheme} data-testid="theme-toggle" title="Hell/Dunkel umschalten"
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="outline-none rounded-full">
              <Avatar className="h-9 w-9 border border-slate-200">
                {user?.picture && <AvatarImage src={user.picture} />}
                <AvatarFallback className="bg-slate-900 text-white text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold text-slate-900">{user?.name}</div>
                <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                <div className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold uppercase" data-testid="user-role-badge">{user?.role_label || (isAdmin ? "Administrator" : "Benutzer")}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.is_platform_admin && (
                <DropdownMenuItem onClick={() => navigate("/plattform")} data-testid="plattform-link">
                  <Building2 className="h-4 w-4 mr-2" />Plattform-Verwaltung
                </DropdownMenuItem>
              )}
              {user?.is_platform_admin && (
                <DropdownMenuItem onClick={() => navigate("/admin/anfragen")} data-testid="access-requests-link">
                  <UserCheck className="h-4 w-4 mr-2" />Zugangsanfragen
                  {accessCount > 0 && <span data-testid="access-requests-count" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold leading-5 text-center">{accessCount}</span>}
                </DropdownMenuItem>
              )}
              {user?.is_platform_admin && (
                <DropdownMenuItem onClick={() => navigate("/admin/meldungen")} data-testid="problem-reports-link">
                  <Bug className="h-4 w-4 mr-2" />Problem-Meldungen
                </DropdownMenuItem>
              )}
              {user?.is_platform_admin && (
                <DropdownMenuItem onClick={() => navigate("/admin/abos")} data-testid="admin-abos-link">
                  <CreditCard className="h-4 w-4 mr-2" />Abos aller Firmen
                </DropdownMenuItem>
              )}
              {user?.is_platform_admin && (
                <DropdownMenuItem onClick={() => navigate("/admin/individuell")} data-testid="admin-individuell-link">
                  <BadgeEuro className="h-4 w-4 mr-2" />Individuell-Angebot
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <>
                  <DropdownMenuItem onClick={() => navigate("/benutzer")} data-testid="benutzer-link">
                    <Users className="h-4 w-4 mr-2" />Benutzerverwaltung
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/stammdaten")} data-testid="stammdaten-link">
                    <Settings className="h-4 w-4 mr-2" />Stammdaten
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/einstellungen/abo")} data-testid="abo-link">
                    <CreditCard className="h-4 w-4 mr-2" />Abo
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  if (navigator.onLine) { prefetchAll(true); toast.info("Offline-Daten werden aktualisiert …"); }
                  else { toast.error("Keine Verbindung – Offline-Aktualisierung nicht möglich."); }
                }}
                data-testid="offline-refresh-link">
                <CloudDownload className="h-4 w-4 mr-2" />Offline aktualisieren
              </DropdownMenuItem>
              {accounts.length > 0 && (
                <DropdownMenuItem onClick={() => setAccountsOpen("menu")} data-testid="my-accounts-link">
                  <Users className="h-4 w-4 mr-2" />Meine Konten{accounts.length > 1 ? ` (${accounts.length})` : ""}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={startTour} data-testid="tour-restart-btn">
                <Compass className="h-4 w-4 mr-2" />Rundgang (Tour) starten
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowNavSettings(true)} data-testid="nav-settings-link">
                <SlidersHorizontal className="h-4 w-4 mr-2" />Menü einstellen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/hilfe")} data-testid="help-link">
                <LifeBuoy className="h-4 w-4 mr-2" />Hilfe
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/impressum")} data-testid="menu-impressum-link">
                <Scale className="h-4 w-4 mr-2" />Impressum
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/datenschutz")} data-testid="menu-datenschutz-link">
                <ShieldCheck className="h-4 w-4 mr-2" />Datenschutz
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowProblem(true)} data-testid="problem-report-link">
                <Bug className="h-4 w-4 mr-2" />Probleme melden
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => refreshApp()} data-testid="profile-refresh-app-btn">
                <RefreshCw className="h-4 w-4 mr-2" />Aktualisieren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowVersion(true)} data-testid="version-info-link">
                <Info className="h-4 w-4 mr-2" />Version {APP_VERSION}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" />Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={showVersion} onOpenChange={setShowVersion}>
            <DialogContent className="max-w-md" data-testid="version-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                    <HardHat className="h-4 w-4 text-white" />
                  </div>
                  PrüfDoc Pro · Version {APP_VERSION}
                </DialogTitle>
                <DialogDescription>Stand {BUILD_DATE} · Neuerungen in dieser Version</DialogDescription>
              </DialogHeader>
              <ul className="space-y-2.5 mt-1" data-testid="version-changelog">
                {CHANGELOG.slice(-CHANGELOG_MAX).map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </DialogContent>
          </Dialog>
          </div>
        </div>
        <BillingBanner isAdmin={isAdmin} />
      </header>
      <AccountsDialog open={!!accountsOpen} onOpenChange={(v) => !v && setAccountsOpen(null)} accounts={accounts}
        reload={reloadAccounts} isAdmin={user?.role === "admin"} chooser={accountsOpen === "chooser"} />
      <ProblemReportDialog open={showProblem} onOpenChange={setShowProblem} user={user} />
      <NavSettingsDialog open={showNavSettings} onOpenChange={setShowNavSettings}
        items={orderedNav.slice(1)} isMore={isMore} />
      <main className="max-w-[1400px] mx-auto px-4 py-6 sm:py-8 pb-6 md:pb-8">{children}</main>
      <LegalFooter className="pb-24 md:pb-6" />
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 flex overflow-x-auto" data-testid="mobile-bottom-nav">
        {withMore(navItems, (n) => {
          const active = location.pathname.startsWith(n.to);
          return (
            <Link key={n.to} to={n.to} data-testid={`mobilenav-${n.id || n.label.toLowerCase()}`}
              className={`relative shrink-0 w-[76px] min-h-[58px] flex flex-col items-center justify-center gap-0.5 py-2 transition-colors ${active ? "text-primary" : "text-slate-500"}`}>
              <n.icon className="h-5 w-5" />
              <span className="text-[10px] font-semibold">{n.short || n.label}</span>
              {badgeFor(n) > 0 && <NavBadge n={badgeFor(n)} testid="mobilenav-loeschanfragen-count" mobile />}
              {photoBadge(n, true)}
            </Link>
          );
        }, (more) => <MoreMenu items={more} mobile pathname={location.pathname} />)}
      </nav>
    </div>
  );
}
